import { AsYouType, isPossiblePhoneNumber } from 'libphonenumber-js';
import { getCountryMetadataByCallingCode, getCountryMetadataByCountryCode, KNOWN_CALLING_CODES, } from './phone-countries';
const MAX_E164_DIGITS = 15;
const FORCE_PRIMARY_COUNTRY_BY_CALLING_CODE = new Set(['44']);
const hasKnownCallingCodePrefix = (digits) => {
    if (!digits) {
        return false;
    }
    return KNOWN_CALLING_CODES.some((code) => code.startsWith(digits) || digits.startsWith(code));
};
const findCallingCodeFromDigits = (digits) => {
    if (!digits) {
        return null;
    }
    return KNOWN_CALLING_CODES.find((code) => digits.startsWith(code)) || null;
};
const buildDetectionCode = (callingCode, digits, countryCode) => {
    if (!digits.startsWith(callingCode)) {
        return callingCode;
    }
    if (callingCode !== '1') {
        return callingCode;
    }
    if (!countryCode || countryCode === 'US' || countryCode === 'CA') {
        return callingCode;
    }
    const nationalDigits = digits.slice(callingCode.length);
    if (nationalDigits.length < 3) {
        return callingCode;
    }
    return `${callingCode}${nationalDigits.slice(0, 3)}`;
};
const detectCountry = (value) => {
    if (!value.startsWith('+')) {
        return null;
    }
    const digits = extractDigits(value);
    if (!digits) {
        return null;
    }
    const formatter = new AsYouType();
    formatter.input(`+${digits}`);
    const detectedCountryCode = formatter.getCountry();
    const detectedCallingCode = formatter.getCallingCode() || findCallingCodeFromDigits(digits);
    if (!detectedCallingCode) {
        return null;
    }
    const metadata = FORCE_PRIMARY_COUNTRY_BY_CALLING_CODE.has(detectedCallingCode)
        ? getCountryMetadataByCallingCode(detectedCallingCode)
        : (detectedCountryCode && getCountryMetadataByCountryCode(detectedCountryCode)) ||
            getCountryMetadataByCallingCode(detectedCallingCode);
    if (!metadata) {
        return null;
    }
    return {
        countryCode: FORCE_PRIMARY_COUNTRY_BY_CALLING_CODE.has(detectedCallingCode) ? metadata.code : detectedCountryCode,
        detectionCode: buildDetectionCode(detectedCallingCode, digits, detectedCountryCode),
        metadata,
    };
};
const formatWithTemplate = (digits, template) => {
    let formatted = '';
    let digitIndex = 0;
    for (let index = 0; index < template.length; index += 1) {
        const char = template[index];
        if (char === 'X') {
            if (digitIndex < digits.length) {
                formatted += digits[digitIndex];
                digitIndex += 1;
                continue;
            }
            return {
                formatted,
                ghostText: template.slice(index),
            };
        }
        if (char === '+') {
            formatted += '+';
            continue;
        }
        if (digitIndex > 0 && digitIndex < digits.length) {
            formatted += char;
            continue;
        }
        return {
            formatted,
            ghostText: template.slice(index),
        };
    }
    return {
        formatted,
        ghostText: '',
    };
};
const getMaxTotalDigits = (value) => {
    const detection = detectCountry(value);
    if (!detection) {
        return MAX_E164_DIGITS;
    }
    return Math.min(MAX_E164_DIGITS, detection.metadata.callingCode.length + detection.metadata.maxNationalDigits);
};
const asYouTypeFormat = (value) => {
    const digits = extractDigits(value);
    if (!digits) {
        return value.startsWith('+') ? '+' : '';
    }
    return value.startsWith('+') ? new AsYouType().input(`+${digits}`) : digits;
};
export const extractDigits = (value) => value.replace(/\D/g, '');
export const normalizeSmartPhoneInput = (input, previousValue) => {
    const digits = extractDigits(input);
    const startsWithPlus = input.trim().startsWith('+');
    if (!digits) {
        return startsWithPlus ? '+' : '';
    }
    if (startsWithPlus || (previousValue?.startsWith('+') && digits.length > 0)) {
        return `+${digits}`;
    }
    if (digits.length > 10) {
        return `+${digits}`;
    }
    return digits;
};
export const normalizePastedPhoneInput = (input) => {
    const digits = extractDigits(input);
    if (!digits) {
        return '';
    }
    if (input.includes('+') || digits.length > 10) {
        return `+${digits}`;
    }
    return digits;
};
export const enforceSmartPhoneDigitLimit = (value) => {
    const startsWithPlus = value.startsWith('+');
    const digits = extractDigits(value);
    if (!digits) {
        return startsWithPlus ? '+' : '';
    }
    const maxDigits = getMaxTotalDigits(value);
    const trimmedDigits = digits.slice(0, maxDigits);
    return startsWithPlus ? `+${trimmedDigits}` : trimmedDigits;
};
export const countDigitsBeforeCursor = (value, cursor) => {
    return extractDigits(value.slice(0, Math.max(0, cursor))).length;
};
export const getCursorPositionForDigitIndex = (value, digitIndex) => {
    if (digitIndex <= 0) {
        return value.startsWith('+') ? 1 : 0;
    }
    let seenDigits = 0;
    for (let index = 0; index < value.length; index += 1) {
        if (/\d/.test(value[index])) {
            seenDigits += 1;
            if (seenDigits === digitIndex) {
                return index + 1;
            }
        }
    }
    return value.length;
};
export const removeDigitAtIndex = (digits, indexToDelete) => {
    if (indexToDelete < 0 || indexToDelete >= digits.length) {
        return digits;
    }
    return `${digits.slice(0, indexToDelete)}${digits.slice(indexToDelete + 1)}`;
};
export const analyzeSmartPhoneInput = (value) => {
    const normalizedValue = enforceSmartPhoneDigitLimit(normalizeSmartPhoneInput(value));
    const startsWithPlus = normalizedValue.startsWith('+');
    const digits = extractDigits(normalizedValue);
    const country = detectCountry(normalizedValue);
    const callingCode = findCallingCodeFromDigits(digits);
    const template = country?.metadata.formatTemplate || getCountryMetadataByCallingCode(callingCode || '')?.formatTemplate;
    const isValid = startsWithPlus && isPossiblePhoneNumber(normalizedValue);
    let state = 'empty';
    if (!normalizedValue || normalizedValue === '+') {
        state = 'empty';
    }
    else if (!startsWithPlus) {
        state = 'needs_plus';
    }
    else if (!callingCode) {
        state = hasKnownCallingCodePrefix(digits) ? 'partial_country' : 'unknown_code';
    }
    else if (digits.length <= callingCode.length) {
        state = 'partial_country';
    }
    else {
        state = isValid ? 'valid' : 'country_detected';
    }
    let displayValue = startsWithPlus ? asYouTypeFormat(normalizedValue) : normalizedValue;
    let ghostText = '';
    if (template && startsWithPlus && state !== 'unknown_code') {
        const formatResult = formatWithTemplate(digits, template);
        displayValue = formatResult.formatted;
        ghostText = formatResult.ghostText;
    }
    else if (normalizedValue === '+') {
        ghostText = 'X XXX XXX XXXX';
    }
    return {
        value: normalizedValue,
        displayValue,
        ghostText,
        state,
        isValid,
        country,
        formatTemplate: template || null,
    };
};
